import { useRef, useCallback, useMemo, useEffect } from 'react';

export interface OnResizeFunctionParameters {
  width?: number;
  height?: number;
}

export type OnResizeFunction = (value: OnResizeFunctionParameters) => void;

const useResizeObserver = (onResize: OnResizeFunction, isResizeListenerEnabled = true) => {
  const elementRef = useRef<HTMLElement | null>(null);

  const handleResize = useCallback(() => {
    if (elementRef.current) {
      const { height, width } = elementRef.current.getBoundingClientRect();
      onResize({ height, width });
    }
  }, [onResize]);

  const resizeObserver = useMemo(() => {
    return new ResizeObserver((entries) => {
      if (elementRef.current) {
        for (const entry of entries) {
          if (entry.target === elementRef.current) {
            handleResize();
          }
        }
      }
    });
  }, [handleResize]);

  useEffect(() => {
    return () => {
      resizeObserver.disconnect();
    };
  }, [resizeObserver]);

  const setRef = useCallback(
    (el: HTMLElement | null) => {
      if (elementRef.current) {
        resizeObserver.unobserve(elementRef.current);
        // set the element reference to null in case isResizeListenerEnabled is set to false
        elementRef.current = null;
      }

      if (isResizeListenerEnabled) {
        elementRef.current = el;
        if (el) {
          resizeObserver.observe(el);
          handleResize(); // Trigger a resize when the ref is set
        }
      }
    },
    [handleResize, isResizeListenerEnabled, resizeObserver]
  );

  return [setRef] as const;
};

export default useResizeObserver;
